import {
  Button,
  Content,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Skeleton
} from '@patternfly/react-core';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { buildRuleHelpMarkdown } from './JexlHelpDoc';
import { useRuleDictionary } from './useRuleDictionary';

/** Only ever one guide on screen, so a fixed id is safe. */
const TITLE_ID = 'rule-help-modal-title';

/**
 * The guide, read from the service every time it is opened - which is the point of it being a
 * dialog rather than a constant: the reference half lists the functions and fields the service
 * really has, so it cannot drift from what the editor completes.
 */
const RuleHelpContent: React.FunctionComponent = () => {
  const { dictionary, isLoading } = useRuleDictionary();

  if (isLoading) {
    return <Skeleton height="400px" screenreaderText="Loading the rule language guide" />;
  }

  return (
    // PatternFly's Content is what styles the headings, lists and inline code the markdown renders
    // as; without it they arrive as bare HTML.
    <Content>
      <ReactMarkdown
        components={{
          // An example longer than the dialog is wide is otherwise cut off mid-argument, with
          // nothing to say the rest of the line exists.
          pre: ({ children }) => (
            <pre
              style={{
                overflowX: 'auto',
                padding: '8px 12px',
                borderRadius: '4px',
                backgroundColor: 'var(--pf-t--global--background--color--secondary--default, #f2f2f2)'
              }}
            >
              {children}
            </pre>
          )
        }}
      >
        {buildRuleHelpMarkdown(dictionary)}
      </ReactMarkdown>
    </Content>
  );
};

export interface RuleHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * A dialog rather than the popover this used to be: the guide is several screens long, and a
 * popover anchored to a help icon inside a form gave it a 500px box to be read in.
 */
export const RuleHelpModal: React.FunctionComponent<RuleHelpModalProps> = ({ isOpen, onClose }) => (
  // Left to itself PatternFly points aria-labelledby at the modal box, so the dialog is announced
  // as its entire contents. Naming the title element gives it the heading instead.
  <Modal variant={ModalVariant.medium} isOpen={isOpen} onClose={onClose} aria-labelledby={TITLE_ID}>
    <ModalHeader title="Rule language guide" labelId={TITLE_ID} />
    <ModalBody>{isOpen && <RuleHelpContent />}</ModalBody>
    <ModalFooter>
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
);
